import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { theme } from '../../styles/theme';
import { DailyTask, CategorizedTasks } from '../../hooks/useDailyRoutines';
import { toUniversalLink, withPresentOverWebView } from '../../utils/deeplinkMapper';
import { useTranslation } from '../../i18n';
// @ts-ignore - canvas-confetti doesn't have types
import confetti from 'canvas-confetti';
import {
  trackTaskOpened,
  trackTaskCompleted,
  mapTaskType,
  mapTimeCategoryToTime,
  mapContentCategoryToContentType,
  type TaskProps,
} from '../../services/analytics.service';

function buildTaskProps(task: DailyTask): TaskProps {
  return {
    type: mapTaskType({
      type: task.type,
      deeplink: task.deeplink,
      contentCategory: (task as any).contentCategory,
    }),
    contentType: mapContentCategoryToContentType(
      (task as any).contentCategory || task.contentType
    ),
    contentID: (task as any).contentId || (task as any).content_id || null,
    taskTitle: task.title || '',
    time: mapTimeCategoryToTime(task.timeCategory),
  };
}

interface DailyRoutinesViewProps {
  weeklyRoutines: { [date: string]: DailyTask[] };
  categorizedRoutines: { [date: string]: CategorizedTasks };
  isLoading: boolean;
  isGenerating?: boolean;
  onCompleteTask: (date: string, taskId: string) => void;
  token?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DayNavigator = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 16px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--color-border);
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const DayTabWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
`;

const ProgressRing = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  transform: rotate(-90deg);
`;

const LoadingRing = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  animation: spin-ring 1.2s linear infinite;

  @keyframes spin-ring {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DayTab = styled.button<{ isActive: boolean; isToday: boolean; isDisabled: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 42px;
  height: 42px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${(props) =>
    props.isDisabled
      ? 'var(--color-bg-elevated)'
      : props.isActive
        ? 'var(--color-primary)'
        : 'var(--color-bg-elevated)'};
  color: ${(props) => props.isDisabled ? 'var(--color-border)' : props.isActive ? '#FFFFFF' : 'var(--color-text)'};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: ${(props) => (props.isToday ? '600' : '500')};
  cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) =>
      props.isDisabled
        ? 'var(--color-bg-elevated)'
        : props.isActive
          ? 'var(--color-primary)'
          : 'var(--color-border)'};
  }
`;

const TasksContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 40px;
`;

const TimeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TimeSectionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin: 0;
  padding-bottom: 8px;
`;

const TaskItem = styled.div<{ completed: boolean; isHealth?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: ${(props) => props.isHealth
    ? 'linear-gradient(135deg, rgba(127, 169, 246, 0.15) 0%, rgba(193, 159, 236, 0.15) 100%)'
    : 'var(--color-bg-elevated)'};
  border: ${(props) => props.isHealth ? '1px solid rgba(127, 169, 246, 0.2)' : 'none'};
  border-radius: 12px;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.completed ? 0.5 : 1)};
`;

const HealthIcon = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const Checkbox = styled.div<{ checked: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid ${(props) => (props.checked ? 'var(--color-primary)' : 'var(--color-text-muted)')};
  border-radius: 6px;
  background: ${(props) => (props.checked ? 'var(--color-primary)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &::after {
    content: '✓';
    color: var(--color-text);
    font-size: 14px;
    font-weight: bold;
    opacity: ${(props) => (props.checked ? 1 : 0)};
    transition: opacity 0.2s ease;
  }
`;

const TaskContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TaskTitle = styled.p<{ completed: boolean }>`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  margin: 0;
  text-decoration: ${(props) => (props.completed ? 'line-through' : 'none')};
`;

const TaskDescription = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  color: var(--color-text-muted);
  margin: 0;
`;

const ContentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

const ContentTitle = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 12px;
  color: var(--color-primary);
  font-weight: 500;
`;

const ContentTypeBadge = styled.span`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 10px;
  font-weight: 600;
  color: var(--color-text);
  background: var(--color-border-subtle);
  padding: 2px 8px;
  border-radius: 6px;
`;

const OpenButton = styled.a`
  flex-shrink: 0;
  padding: 6px 16px;
  background: var(--color-primary);
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    background: var(--color-primary-dark);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  color: var(--color-text-muted);
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-border-subtle);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Map raw content_type from API to i18n key
const CONTENT_TYPE_I18N_MAP: Record<string, string> = {
  meditation: 'content.meditation',
  story: 'content.story',
  music: 'content.music',
  blog: 'content.article',
  talk: 'content.article',
  yoga: 'content.yoga',
  theme: 'content.theme',
  playlist: 'content.playlist',
  category: 'content.category',
};

const TIME_CATEGORY_KEYS = [
  { key: 'sabah', labelKey: 'routines.morning' },
  { key: 'ogle', labelKey: 'routines.afternoon' },
  { key: 'aksam', labelKey: 'routines.evening' },
  { key: 'zamansiz', labelKey: 'routines.anytime' },
] as const;

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = date.getDate();
  return `${day}`;
};

// Generate 7-day date range starting from today
const getWeekDates = (): string[] => {
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(todayLocal);
    d.setDate(todayLocal.getDate() + i);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }
  return dates;
};

export const DailyRoutinesView: React.FC<DailyRoutinesViewProps> = ({
  weeklyRoutines,
  categorizedRoutines,
  isLoading,
  isGenerating = false,
  onCompleteTask,
  token,
}) => {
  const { t } = useTranslation();
  const allDates = getWeekDates();
  const loadedDates = new Set(Object.keys(weeklyRoutines));
  const today = allDates[0];

  const [selectedDate, setSelectedDate] = useState(today);

  // If selected date becomes available, keep it; otherwise stay on today
  useEffect(() => {
    if (!loadedDates.has(selectedDate) && loadedDates.has(today)) {
      setSelectedDate(today);
    }
  }, [loadedDates.size, today, selectedDate]);

  // Confetti function
  const triggerConfetti = (event: React.MouseEvent) => {
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    confetti({
      particleCount: 50,
      spread: 60,
      origin: { x, y },
      colors: ['#8B5CF6', '#A78BFA', '#C4B5FD', '#FFFFFF'],
      disableForReducedMotion: true,
    });
  };

  const handleTaskClick = (date: string, taskId: string, completed: boolean, event: React.MouseEvent) => {
    if (!completed) {
      triggerConfetti(event);
      // Analytics: task is transitioning from open → completed right now.
      const tasks = weeklyRoutines[date] || [];
      const task = tasks.find((tt) => tt.id === taskId);
      if (task) trackTaskCompleted(buildTaskProps(task));
    }
    onCompleteTask(date, taskId);
  };

  const getDeeplinkUrl = (deeplink: string | undefined): string | undefined => {
    const url = toUniversalLink(deeplink);
    if (!url) return undefined;
    if (url.includes('wellnesspass-app.meditopia.com')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}token=${encodeURIComponent(token || '')}&theme=dark`;
    }
    return withPresentOverWebView(url);
  };

  // Calculate progress for a date
  const calculateProgress = (date: string): number => {
    const tasks = weeklyRoutines[date] || [];
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter((task) => task.completed).length;
    return (completedTasks / tasks.length) * 100;
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </Container>
    );
  }

  const categorized = categorizedRoutines[selectedDate];
  const selectedDateLoaded = loadedDates.has(selectedDate);

  return (
    <Container>
      <DayNavigator>
        {allDates.map((date) => {
          const isLoaded = loadedDates.has(date);
          const progress = calculateProgress(date);
          const radius = 21;
          const circumference = 2 * Math.PI * radius;
          const strokeDashoffset = circumference - (progress / 100) * circumference;
          const hasProgress = progress > 0 && isLoaded;
          const isComplete = progress === 100;

          const showLoadingRing = !isLoaded && isGenerating;

          return (
            <DayTabWrapper key={date}>
              {hasProgress && (
                <ProgressRing>
                  <circle
                    cx="24"
                    cy="24"
                    r={radius}
                    fill="none"
                    stroke={isComplete ? '#6BD39B' : 'var(--color-border)'}
                    strokeWidth="3"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{ transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease' }}
                  />
                </ProgressRing>
              )}
              {showLoadingRing && (
                <LoadingRing>
                  <circle
                    cx="24"
                    cy="24"
                    r={radius}
                    fill="none"
                    stroke="var(--color-text-secondary)"
                    strokeWidth="2"
                    strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
                    strokeLinecap="round"
                  />
                </LoadingRing>
              )}
              <DayTab
                isActive={selectedDate === date}
                isToday={date === today}
                isDisabled={!isLoaded}
                onClick={() => isLoaded && setSelectedDate(date)}
              >
                {formatDate(date)}
              </DayTab>
            </DayTabWrapper>
          );
        })}
      </DayNavigator>

      {!selectedDateLoaded ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : categorized ? (
        <TasksContainer>
          {TIME_CATEGORY_KEYS.map(({ key, labelKey }) => {
            const label = t(labelKey);
            const tasks = categorized[key as keyof CategorizedTasks] as DailyTask[];
            if (!tasks || tasks.length === 0) return null;

            return (
              <TimeSection key={key}>
                <TimeSectionTitle>{label}</TimeSectionTitle>
                {tasks.map((task) => (
                  <TaskItem key={task.id} completed={task.completed} isHealth={task.type === 'health'}>
                    {task.type === 'health' ? (
                      <HealthIcon>
                        {task.id.includes('bedtime') ? '🌙' : '👟'}
                      </HealthIcon>
                    ) : (
                      <Checkbox
                        checked={task.completed}
                        onClick={(e) => handleTaskClick(selectedDate, task.id, task.completed, e)}
                        style={{ cursor: 'pointer' }}
                      />
                    )}
                    <TaskContent>
                      <TaskTitle completed={task.completed}>{task.title}</TaskTitle>
                      {task.description && <TaskDescription>{task.description}</TaskDescription>}
                      {task.contentTitle && (
                        <ContentInfo>
                          <ContentTitle>{task.contentTitle}</ContentTitle>
                          {task.contentType && <ContentTypeBadge>{t(CONTENT_TYPE_I18N_MAP[task.contentType.toLowerCase()] || '') || task.contentType}</ContentTypeBadge>}
                        </ContentInfo>
                      )}
                    </TaskContent>
                    {task.deeplink && task.type !== 'health' && getDeeplinkUrl(task.deeplink) && (
                      <OpenButton
                        href={getDeeplinkUrl(task.deeplink)}
                        onClick={(e) => {
                          e.stopPropagation();
                          trackTaskOpened(buildTaskProps(task));
                          if (!task.completed) {
                            triggerConfetti(e);
                            trackTaskCompleted(buildTaskProps(task));
                            onCompleteTask(selectedDate, task.id);
                          }
                        }}
                      >
                        {t('routines.open')}
                      </OpenButton>
                    )}
                  </TaskItem>
                ))}
              </TimeSection>
            );
          })}

          {categorized.all.length === 0 && (
            <EmptyState>
              <EmptyStateText>{t('routines.noTasks')}</EmptyStateText>
            </EmptyState>
          )}
        </TasksContainer>
      ) : (
        <EmptyState>
          <EmptyStateText>{t('routines.noTasks')}</EmptyStateText>
        </EmptyState>
      )}
    </Container>
  );
};
